import type { ReactNode } from 'react';
import { useAuthStore } from './auth-store';

export type UserRole =
  | 'super_admin' | 'admin' | 'sales_manager' | 'sales_agent' | 'accountant' | 'viewer';

export const ADMIN_ROLES: UserRole[]      = ['super_admin', 'admin'];
export const MANAGER_ROLES: UserRole[]    = ['super_admin', 'admin', 'sales_manager'];
export const AGENT_ROLES: UserRole[]      = ['super_admin', 'admin', 'sales_manager', 'sales_agent'];
export const ACCOUNTANT_ROLES: UserRole[] = ['super_admin', 'admin', 'accountant'];
export const ALL_EXCEPT_VIEWER: UserRole[] = ['super_admin', 'admin', 'sales_manager', 'sales_agent', 'accountant'];

export function useCurrentUser() {
  return useAuthStore((s) => s.user);
}

export function useRole(): UserRole | null {
  return useAuthStore((s) => s.user?.role ?? null);
}

export function useHasRole(roles: UserRole[]): boolean {
  const role = useRole();
  return role !== null && roles.includes(role);
}

/**
 * Renders children only when the current user has one of the given roles.
 *
 * NOTE: RoleGate is always a SINGLE element to its parent. If you place it as a
 * direct child of antd <Space> with MULTIPLE children inside, those children
 * will not receive Space's gap (they collapse together). In that case either:
 *   - wrap the inner elements in their own <Space>, or
 *   - use one RoleGate per element so each is a direct, spaced child of <Space>.
 */
export function RoleGate({ roles, children, fallback = null }: {
  roles: UserRole[];
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const allowed = useHasRole(roles);
  return <>{allowed ? children : fallback}</>;
}
